import { brandsOpenApi } from '@constants';
import { CAMPAIGN_STATUS } from '@enums';
import { axiosBoa } from '@utils';
import { getParent, types } from 'mobx-state-tree';
import { models } from './constants';
import { createStoreAction, parseContentDispositionFilename } from './utils';

export const OrdersStore = types
    .model('OrdersStore', {
        orderList: types.array(models.OrderModel),
        campaignsWithoutBoosts: types.array(models.CampaignModel),
        meta: types.maybeNull(
            types.model({
                page: types.maybeNull(types.number),
                perPage: types.maybeNull(types.number),
                total: types.maybeNull(types.number),
                totalPages: types.maybeNull(types.number)
            })
        ),
        filters: types.model({
            klickly_status: types.maybe(types.array(types.number)),
            financial_status: types.maybe(types.array(types.number)),
            fulfillment_status: types.maybe(types.array(types.number)),
            ecommerce_status: types.maybe(types.array(types.number)),
            created_at_min: types.maybe(types.string),
            created_at_max: types.maybe(types.string)
        }),
        sorters: types.model({
            sort: 'ecommerce_created_at',
            order: 'desc'
        }),
        pending: false,
        orderDetails: types.maybeNull(models.OrderModel),
        orderDetailsPending: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            setPending(pending) {
                self.pending = pending;
            },
            resetFilters() {
                self.filters = {};
            },
            updateFilters(filters) {
                Object.assign(self.filters, filters);
            },
            resetSorters() {
                self.sorters = {
                    sort: 'ecommerce_created_at',
                    order: 'desc'
                };
            },
            updateSorters(sorters) {
                Object.assign(self.sorters, sorters);
            },
            onDateRangeChange: () => {
                self.orderList = [];
            },
            downloadOrders: action(
                function* downloadOrders(params = {}) {
                    self.pending = true;
                    Object.assign(
                        params,
                        {
                            page: self.meta.page || 1,
                            limit: self.meta.perPage || 10,
                            download: true,
                            timezone_offset: self.root.performanceStore?.timezoneOffset ?? 0
                        },
                        self.filters,
                        self.sorters
                    );

                    const { data, headers } = yield axiosBoa.get(brandsOpenApi.orders.list, {
                        fullResponse: true,
                        params
                    });

                    // Check if response is a file (CSV) or async processing message
                    const contentDisposition = headers['content-disposition'];
                    if (contentDisposition) {
                        // Direct CSV download
                        const fileName = parseContentDispositionFilename(contentDisposition);
                        return { type: 'file', file: data, fileName };
                    } else if (data && typeof data === 'object') {
                        // Check if modal is required
                        if (data.requiresEmailModal) {
                            return {
                                type: 'modal',
                                userEmail: data.userEmail,
                                estimatedRecords: data.estimatedRecords
                            };
                        }
                        // Async processing response
                        return {
                            type: 'message',
                            message: data?.message,
                            status: data?.status
                        };
                    } else {
                        throw new Error('Unexpected response format from server');
                    }
                },
                { onFinally: () => (self.pending = false) }
            ),
            onDownloadOrderList: () => {
                return self.orderList;
            },
            getOrders: action(
                function* getOrders({ params = {} } = {}) {
                    self.pending = true;
                    const query = {
                        ...self.filters,
                        ...self.sorters,
                        timezone_offset: self.root.performanceStore?.timezoneOffset ?? 0,
                        ...params
                    };

                    const response = yield axiosBoa.get(brandsOpenApi.orders.list, { params: query });
                    self.orderList = response.data;
                    self.meta = response.meta;
                },
                { onFinally: () => (self.pending = false) }
            ),
            resetOrderDetails: () => {
                self.orderDetails = null;
            },
            getOrderByUUID: action(
                function* getOrderByUUID(klicklyOrderUUID) {
                    self.orderDetailsPending = true;
                    self.orderDetails = null;

                    const response = yield axiosBoa.get(brandsOpenApi.orders.get(klicklyOrderUUID));
                    self.orderDetails = response;
                },
                { onFinally: () => (self.orderDetailsPending = false) }
            ),
            getCampaignsWithoutBoosts: action(function* getCampaignsWithoutBoosts() {
                const response = yield axiosBoa.get(brandsOpenApi.campaigns.list, {
                    params: {
                        sort: 'title',
                        statuses: [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.CREATED],
                        has_boost: false
                    }
                });
                self.campaignsWithoutBoosts = response.data;
            }),
            afterCreate: () => {
                self.resetSorters();
            }
        };
    });
